import { FC, useState } from 'react'
import { ActivitiesTabProps } from '../types/tabTypes'
import { BookingContent } from '../../../../../Types/HotelBookingTypes'
import { i18n } from '../../../../translate/i18n'

const ActivitiesTab: FC<ActivitiesTabProps> = ({ atividades }) => {
  const [expandedActivities, setExpandedActivities] = useState<Set<number>>(new Set())

  const toggleActivity = (activityId: number) => {
    setExpandedActivities(prev => {
      const newSet = new Set(prev)
      if (newSet.has(activityId)) {
        newSet.delete(activityId)
      } else {
        newSet.add(activityId)
      }
      return newSet
    })
  }
  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return i18n.t('hospitality_room_reservation_tabs.services.status_confirmed')
      case 'pending':
        return i18n.t('hospitality_room_reservation_tabs.services.status_pending')
      case 'cancelled':
        return i18n.t('hospitality_room_reservation_tabs.services.status_cancelled')
      case 'completed':
        return i18n.t('hospitality_room_reservation_tabs.services.status_completed')
      default:
        return status
    }
  }

  if (atividades.length === 0) {
    return (
      <div className='text-center py-10'>
        <i className='fas fa-running text-muted fs-3x mb-4'></i>
        <h3 className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.activities.label_no_data')}</h3>
        <p className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.activities.label_pending')}</p>
      </div>
    )
  }

  return (
    <div className='card card-xxl-stretch'>
      <div className='card-header py-5'>
        <h3 className='card-title'>
          <i className='fas fa-running me-2'></i>
          {i18n.t('hospitality_room_reservation_tabs.activities.tab_title')}
        </h3>
        <div className='card-toolbar'>
          <span className='badge badge-light-success'>
            {atividades.length} {atividades.length === 1 ? i18n.t('hospitality_room_reservation_tabs.activities.label_activity') : i18n.t('hospitality_room_reservation_tabs.activities.label_activities')}
          </span>
        </div>
      </div>
      <div className='card-body'>
        <div className='row'>
          {atividades.map((atividade) => {
            const content: BookingContent = JSON.parse(atividade.content || '{}')
            const isExpanded = expandedActivities.has(atividade.id)
            return (
              <div key={atividade.id} className='col-12 mb-4'>
                <div className='card card-flush border border-gray-300'>
                  <div 
                    className='card-header bg-light-success' 
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleActivity(atividade.id)}
                  >
                    <div className='d-flex justify-content-between align-items-center w-100'>
                      <div className='d-flex align-items-center'>
                        <i className='fas fa-running text-success fs-2x me-3'></i>
                        <div>
                          <h4 className='card-title text-success mb-1'>{atividade.hotelActivity.title}</h4>
                          <div className='d-flex align-items-center flex-wrap gap-3'>
                            <span className='text-muted'>
                              <i className='fas fa-calendar me-1'></i>
                              {new Date(atividade.reservationDate).toLocaleDateString('pt-BR')}
                            </span>
                            <span className='text-muted'>
                              <i className='fas fa-users me-1'></i>
                              {atividade.participants} {i18n.t('hospitality_room_reservation_tabs.activities.label_people')}
                            </span>
                            <span className='text-muted'>
                              <i className='fas fa-map-marker-alt me-1'></i>
                              {atividade.hotelActivity.location}
                            </span>
                            <span className='text-muted'>
                              <i className='fas fa-clock me-1'></i>
                              {atividade.hotelActivity.time}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className='d-flex align-items-center'>
                        <div className='text-end me-3'>
                          <h4 className='text-success mb-0'>{atividade.currency} {parseFloat(atividade.totalAmount).toFixed(2)}</h4>
                        </div>
                        <span className={`badge badge-${atividade.status === 'confirmed' ? 'success' : atividade.status === 'cancelled' ? 'danger' : 'warning'} me-3`}>
                          {getStatusLabel(atividade.status)}
                        </span>
                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-muted`}></i>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                  <div className='card-body'>
                    <div className='row mb-4'>
                      <div className='col-12'>
                        <h5 className='text-gray-700 fw-bold mb-3'>
                          <i className='fas fa-info-circle text-primary me-2'></i>
                        </h5>
                        <div 
                          className='text-gray-600'
                          dangerouslySetInnerHTML={{ __html: atividade.hotelActivity.description }}
                        />
                      </div>
                    </div>

                    <div className='row'>
                      <div className='col-md-3 mb-3'>
                        <small className='text-muted d-block'>{i18n.t('hospitality_room_reservation_tabs.activities.label_category')}</small>
                        <div className='fw-bold'>{atividade.hotelActivity.category}</div>
                      </div>
                      {content.sessionTime && (
                        <div className='col-md-3 mb-3'>
                          <small className='text-muted d-block'>{i18n.t('hospitality_room_reservation_tabs.activities.label_session_time')}</small>
                          <div className='fw-bold'>{content.sessionTime}</div>
                        </div>
                      )}
                      {content.skillLevel && (
                        <div className='col-md-3 mb-3'>
                          <small className='text-muted d-block'>{i18n.t('hospitality_room_reservation_tabs.activities.label_skill_level')}</small>
                          <div className='fw-bold text-capitalize'>{content.skillLevel}</div>
                        </div>
                      )}
                    </div>

                    {atividade.specialRequests && (
                      <div className='mt-4 p-3 bg-light-info rounded'>
                        <small className='text-muted d-block'>{i18n.t('hospitality_room_reservation_tabs.activities.label_special_requests')}</small>
                        <div className='fw-bold text-info'>{atividade.specialRequests}</div>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ActivitiesTab